import { BaseMap } from './BaseMap.js';
import { OptimizedResizeInstance } from 'js/components/Util/OptimizedResize.js';

export class MapboxGL extends BaseMap {
  static initClass() {
    this.instances = [];
    this.providerLoaded = false;
    this.className = "Yext.Maps.MapboxGL";
  }
  constructor(args) {
    super(args);
    const styleId = this.config.mapboxMapIdentifier || 'mapbox/streets-v12';
    this.mapstyle = `mapbox://styles/${styleId}`;
    this.options = {
      container: this.element.id,
      style: this.mapstyle,
      zoom: this.config.zoom,
      interactive: !this.config.disableMapControl
    };
    this.boundsOptions = {
      linear: true,
      duration: 0,
      animate: false,
      padding: this.BUFFER,
      maxZoom: 14
    };
    this.pins = [];
    this.bounds = [];
    this.pinWidth = 32;
    this.pinHeight = 38;
  }

  appendScript() {
    let baseUrl = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl";
    let link = document.createElement('link');
    let mapScript = document.createElement('script');
    link.rel = 'stylesheet';
    link.href = `${baseUrl}.css`;
    mapScript.src = `${baseUrl}.js`;
    mapScript.onload = () => {
      this.constructor.providerCallback();
    };
    document.head.appendChild(link);
    document.head.appendChild(mapScript);
  }

  mapboxToken() {
    const DEFAULT = 'pk.eyJ1IjoieWV4dCIsImEiOiJjbXRzd2tucDYwMG83MnlvaW55MDk1MGVwIn0.TUJuC4hFy7XpgBRxtfiJEA';
    const id = this.config.apiID;
    return (id && id.startsWith('pk.')) ? id : DEFAULT;
  }

  localizeMap(map) {
    if (document.documentElement.hasAttribute('lang')) {
      const whiteList = [
        'en',
        'es',
        'fr',
        'de',
        'ru',
        'zh'
      ];
      const language = document.documentElement.getAttribute('lang');
      if (whiteList.indexOf(language) > -1) {
        const locale = `{name_${language}}`;
        for (let layer of Array.from(map.getStyle().layers)) {
        // HACK: As of v0.37.0 setting the localization label on road labels deletes the labels
        // Therefore, string check to just avoid setting the locale on road labels.
          if (((layer.layout != null ? layer.layout['text-field'] : undefined) != null) && (layer.id.indexOf('road') === -1)) {
            map.setLayoutProperty(layer.id, 'text-field', locale);
          }
        }
      }
    }
  }

  iconImage() {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${this.pinWidth}" height="${this.pinHeight}" viewBox="0 0 32 38">
        <g fill="none" fill-rule="evenodd">
          <path fill="#000000" fill-rule="nonzero" d="M32 15.385c.005 4.986-2.509 9.664-6.74 12.545l-.313.216c-6.318 4.38-7.367 7.202-7.877 8.689C16.804 37.62 16.688 38 16 38c-.687 0-.803-.38-1.068-1.165-.51-1.48-1.56-4.309-7.877-8.682l-.313-.217C.563 23.72-1.693 15.914 1.336 9.236 4.366 2.557 11.85-1.162 19.266.326 26.684 1.813 31.999 8.099 32 15.385z"/>
          <ellipse fill="#FFFFFF" cx="16" cy="15" rx="5" ry="5"/>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;charset=utf8,${encodeURIComponent(svg)}`;
  }

  preparePin(i, loc, map, lngLat) {
    let icon = this.iconImage(loc, i);

    let wrapper = document.createElement('div');
    let link = document.createElement('a');
    link.className = 'custom-icon-link';
    let url = `${this.config.baseUrl}${loc.url}`;
    if (this.config.linkToGetDirections) {
      url = loc.get_directions_url;
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'nofollow noopener noreferrer');
    }
    link.setAttribute('href', url);
    link.setAttribute('data-ya-track', 'drivingdirection');

    let image = document.createElement('img');
    image.setAttribute('alt', `${loc.altTagText}`);
    image.src = icon;
    image.setAttribute('width', `${this.pinWidth}`);
    image.setAttribute('height', `${this.pinHeight}`);

    link.appendChild(image);
    wrapper.appendChild(link);
    wrapper.setAttribute('data-lon', loc.longitude);
    wrapper.setAttribute('data-lat', loc.latitude);
    wrapper.className = 'custom-icon js-custom-icon';

    let marker = new mapboxgl.Marker({element: wrapper, offset: [-this.pinWidth/2, -this.pinHeight/2]}).setLngLat(lngLat);
    return marker;
  }

  prepareMap() {
    mapboxgl.accessToken = this.mapboxToken();
    let marker;
    let map = new mapboxgl.Map(this.options);
    this.bounds = new mapboxgl.LngLatBounds();
    let idx = 0;
    for (let location of Array.from(this.allLocations)) {
      let coord = new mapboxgl.LngLat(location.longitude, location.latitude);
      marker = this.preparePin(idx, location, map, coord);
      this.pins.push(marker);
      this.bounds.extend(coord);
      idx++;
    }

    if (this.allLocations.length > 0) {
      this.options.center = this.bounds.getCenter();
    } else {
      this.options.center = new mapboxgl.LngLat(this.longitude, this.latitude);
      this.options.zomm = 3;

      map.setCenter(this.options.center);
      map.setZoom(this.options.zoom);
    }

    if (!this.config.disableMapControl) {
      map.addControl(new mapboxgl.NavigationControl());
    }

    map.on('load', () => {
      if (this.pins.length > 0) {
        for (marker of Array.from(this.pins)) {
          marker.addTo(map);
        }
        map.fitBounds(this.bounds, this.boundsOptions);
      }

      this.localizeMap(map);

      this.mapLoaded();
    });

    OptimizedResizeInstance.on( () => {
      if (this.allLocations.length == 0) return;
      return map.fitBounds(this.bounds, this.boundsOptions);
    });

    return map;
  }
}

MapboxGL.initClass();
